// Secret Sauce — the automatic "what your top reps do differently" page (view: bespoke-playbook).
//
// No uploads. It reads the persisted per-call scores (GET /api/secret-sauce), which the server has
// already split into your Top-Rep vs Core-Rep cohorts (the CLASS org-entity) and grouped by call-type
// CLUSTER so the comparison is apples-to-apples. This module renders one differentiator table per
// cluster and, on demand, turns each cluster's gaps into a written coaching play via the existing
// /api/playbook synthesis (a live model call — hence the few-second wait, explained inline).
//
// The CLASS layer (assigning reps Top / Core on the Teams page) is the fundamental input today; it is
// the manual stand-in for the CRM/attainment signal that will drive this automatically later. When no
// reps are classified, the page shows a gate that points to Teams instead of an empty table.

import * as api from '../lib/api.js';
import type { SecretSauceCluster, SecretSauceGap, PlaybookSynthesis } from '../lib/api.js';
import { escapeHtml } from '../lib/escape.js';
import { skillLabelById } from '../lib/skills.js';
import { groupLabel } from './trends.js';
import { navigateTo } from './sidenav.js';
import { reportMasthead, renderClose, renderFooter, waitForReportImages } from './report.js';

/** Round a 0–100 final score for display. */
const r0 = (v: number): string => String(Math.round(v));
const CMP_NOTE = 'Average observed score (0 to 5) per skill, top performers vs. core reps, sorted by the biggest gap.';

// Per-cluster coaching-playbook state. Seeded from the server's persisted playbook on each render, so
// the page is always-on (shown by default, no click); `writtenAt` drives "Last updated" and `stale`
// flags that the benchmark changed since it was written.
type SynthState = 'idle' | 'loading' | 'done' | 'error';
const synthByGroup = new Map<string, { state: SynthState; synth: PlaybookSynthesis | null; writtenAt?: number | null; stale?: boolean }>();

let mounted: HTMLElement | null = null;
// The current render's clusters, keyed by group — read by the once-wired delegated click handler so
// re-opening the view never stacks duplicate listeners (which would fire multiple model calls per click).
let clusterByGroupRef = new Map<string, SecretSauceCluster>();

/** Short absolute date for "Last updated" (e.g. "2 Sep 2026"). */
function fmtWrittenAt(ts: number | null | undefined): string {
  if (!ts || !Number.isFinite(ts)) return '';
  try {
    return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

/** A small 0–5 half-circle needle gauge (red/amber/green bands). Self-contained SVG. */
function gaugeSvg(label: string, value: number | null): string {
  const cx = 50;
  const cy = 50;
  const r = 40;
  const pt = (deg: number, rad = r): [number, number] => {
    const a = (deg * Math.PI) / 180;
    return [cx + rad * Math.cos(a), cy - rad * Math.sin(a)];
  };
  const angle = (v: number): number => 180 - (Math.max(0, Math.min(5, v)) / 5) * 180;
  const arc = (from: number, to: number, color: string): string => {
    const [x1, y1] = pt(from);
    const [x2, y2] = pt(to);
    return `<path d="M ${x1.toFixed(1)} ${y1.toFixed(1)} A ${r} ${r} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)}" fill="none" stroke="${color}" stroke-width="7" stroke-linecap="butt" />`;
  };
  // Gauge bands (red / amber / green) on the light CRO Dashboard ground; needle is dark ink.
  const bands = arc(180, angle(2), '#ef6a5e') + arc(angle(2), angle(3.5), '#e0a53a') + arc(angle(3.5), 0, '#2fb574');
  const has = typeof value === 'number';
  const [nx, ny] = pt(has ? angle(value!) : 90, r - 7);
  const needle = has
    ? `<line x1="${cx}" y1="${cy}" x2="${nx.toFixed(1)}" y2="${ny.toFixed(1)}" stroke="#2a2a2a" stroke-width="2.4" stroke-linecap="round" /><circle cx="${cx}" cy="${cy}" r="3.4" fill="#2a2a2a" />`
    : '';
  return `<div class="pb-gauge-unit">
    <svg viewBox="0 0 100 56" class="pb-gauge-mini" aria-hidden="true">${bands}${needle}</svg>
    <div class="pb-gauge-val">${has ? value!.toFixed(1) : 'n/a'}<span>/5</span></div>
    <div class="pb-gauge-lbl">${escapeHtml(label)}</div>
  </div>`;
}

/** The Top-vs-Core comparison table for one cluster, sorted by biggest gap (server-sorted). */
function comparisonTableHtml(gaps: SecretSauceGap[]): string {
  const fmt = (v: number): string => v.toFixed(1);
  const rows = gaps
    .map((g) => {
      const gapCls = g.gap >= 0.75 ? 'gap-hi' : g.gap >= 0.25 ? 'gap-mid' : 'gap-lo';
      const gapTxt = `${g.gap >= 0 ? '+' : ''}${g.gap.toFixed(1)}`;
      return `<tr><td class="pb-cmp-skill">${escapeHtml(skillLabelById(g.skillId))}</td><td>${fmt(g.topAvg)}</td><td>${fmt(g.coreAvg)}</td><td class="pb-cmp-gap ${gapCls}">${gapTxt}</td></tr>`;
    })
    .join('');
  return `<div class="pb-cmp-scroll"><table class="pb-cmp"><thead><tr><th class="pb-cmp-skill">Skill</th><th>Top avg</th><th>Core avg</th><th>Gap</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

/** Per-skill Top/Core averages keyed by lowercased skill name, so a synthesis focus can find its gauges. */
function scoreMap(gaps: SecretSauceGap[]): Map<string, { top: number | null; core: number | null }> {
  const m = new Map<string, { top: number | null; core: number | null }>();
  for (const g of gaps) {
    m.set(g.skillName.trim().toLowerCase(), { top: g.topAvg, core: g.coreAvg });
    m.set(skillLabelById(g.skillId).trim().toLowerCase(), { top: g.topAvg, core: g.coreAvg });
  }
  return m;
}

/** The coaching play's inner content (intro + per-skill focus + "areas to revisit"). No outer heading —
 *  that lives in the collapsible bar built by playBlockHtml(). */
function coachingInnerHtml(s: PlaybookSynthesis, scores: Map<string, { top: number | null; core: number | null }>): string {
  const esc = escapeHtml;
  const focus = s.focus
    .map((f) => {
      const sc = scores.get(f.skill.trim().toLowerCase());
      const gauges = sc ? `<div class="pb-focus-gauges">${gaugeSvg('Top', sc.top)}${gaugeSvg('Core', sc.core)}</div>` : '';
      return `
      <div class="pb-focus">
        <div class="pb-focus-main">
          <div class="pb-focus-skill">${esc(f.skill)}</div>
          ${f.whatTopDo ? `<div class="pb-focus-row"><span class="pb-focus-lbl">What top reps do</span><p>${esc(f.whatTopDo)}</p></div>` : ''}
          ${f.likelyMissing ? `<div class="pb-focus-row"><span class="pb-focus-lbl">Likely missing today</span><p>${esc(f.likelyMissing)}</p></div>` : ''}
          ${f.actions.length ? `<div class="pb-focus-row"><span class="pb-focus-lbl">How to bridge it</span><ul>${f.actions.map((a) => `<li>${esc(a)}</li>`).join('')}</ul></div>` : ''}
        </div>
        ${gauges}
      </div>`;
    })
    .join('');
  const gapsBlock = s.playbookGaps.length
    ? `<div class="pb-gaps"><h5 class="pb-gaps-h">Areas you may want to revisit</h5><ul>${s.playbookGaps.map((g) => `<li>${esc(g)}</li>`).join('')}</ul></div>`
    : '';
  return `${s.intro ? `<p class="pb-coaching-intro">${esc(s.intro)}</p>` : ''}${focus}${gapsBlock}`;
}

/** "Last updated {date}" badge, or empty when unknown. */
function updatedBadgeHtml(writtenAt?: number | null): string {
  const d = fmtWrittenAt(writtenAt);
  return d ? `<span class="ss-play-updated">Last updated ${escapeHtml(d)}</span>` : '';
}
/** "Benchmark changed since — regenerate" hint, or empty when the playbook is current. */
function staleBadgeHtml(stale?: boolean): string {
  return stale
    ? `<span class="ss-play-stale" role="note">Benchmark changed since — <button type="button" class="ss-play-relink" data-regen>regenerate</button></span>`
    : '';
}

/** The coaching playbook block: a bar (collapse toggle + "Last updated" + stale hint + Regenerate +
 *  Copy + Download) over the coaching content, expanded by default. */
function playBlockHtml(cluster: SecretSauceCluster, s: PlaybookSynthesis, meta: { writtenAt?: number | null; stale?: boolean }): string {
  return `<div class="ss-play-bar">
      <button type="button" class="ss-play-toggle" data-toggle aria-expanded="true"><span class="ss-play-caret" aria-hidden="true">▾</span> Coaching playbook</button>
      <div class="ss-play-meta">${updatedBadgeHtml(meta.writtenAt)}${staleBadgeHtml(meta.stale)}</div>
      <div class="ss-play-actions">
        <button type="button" class="ss-play-act${meta.stale ? ' is-stale' : ''}" data-regen>Regenerate</button>
        <button type="button" class="ss-play-act" data-copy>Copy</button>
        <button type="button" class="ss-play-act" data-dl>Download</button>
      </div>
    </div>
    <div class="ss-play-body">${coachingInnerHtml(s, scoreMap(cluster.gaps))}</div>`;
}

/** Plain-text serialization of a coaching play, for Copy / Download. Not HTML — never inserted as such. */
function synthToText(label: string, s: PlaybookSynthesis): string {
  const lines: string[] = [`Coaching playbook — ${label}`, ''];
  if (s.intro) lines.push(s.intro, '');
  for (const f of s.focus) {
    lines.push(f.skill);
    if (f.whatTopDo) lines.push(`  What top reps do: ${f.whatTopDo}`);
    if (f.likelyMissing) lines.push(`  Likely missing today: ${f.likelyMissing}`);
    if (f.actions.length) {
      lines.push('  How to bridge it:');
      for (const a of f.actions) lines.push(`    - ${a}`);
    }
    lines.push('');
  }
  if (s.playbookGaps.length) {
    lines.push('Areas you may want to revisit:');
    for (const g of s.playbookGaps) lines.push(`  - ${g}`);
  }
  return lines.join('\n').trim() + '\n';
}

const LOADING_HTML =
  '<p class="pb-coaching-loading">Writing your coaching playbook… <span class="ss-loading-sub">our model turns these gaps into specific, call-type-appropriate actions — this takes a few seconds.</span></p>';

function messageHtml(title: string, body: string, cta?: { label: string; view: string }): string {
  const btn = cta ? `<button type="button" class="pb-generate ss-cta" data-nav="${escapeHtml(cta.view)}">${escapeHtml(cta.label)}</button>` : '';
  return `<div class="ss-state"><h3 class="ss-state-h">${escapeHtml(title)}</h3><p class="ss-state-p">${body}</p>${btn}</div>`;
}

/** The quantified upside for a cluster: the final-score gap × the core-rep call volume. Hidden when
 *  the gap is negligible (top and core reps finish about the same on these calls). */
function upsideHtml(c: SecretSauceCluster): string {
  const delta = c.topFinal - c.coreFinal;
  if (!(delta >= 1)) return '';
  return `<p class="ss-upside"><span class="ss-upside-tag">Upside</span> On these calls, core reps average <strong>${escapeHtml(r0(c.coreFinal))}/100</strong> vs <strong>${escapeHtml(r0(c.topFinal))}/100</strong> for top reps — a <strong>${escapeHtml(r0(delta))}-point gap</strong> per call (from ${escapeHtml(String(c.coreCalls))} core-rep call${c.coreCalls === 1 ? '' : 's'}). <span class="ss-upside-note">Ongoing, surgical coaching on the areas surfaced here can move the needle for core reps and lift them toward top-rep performance.</span></p>`;
}

/** The Core reps to coach first on a cluster (named, lowest average final there). */
function coachHtml(c: SecretSauceCluster): string {
  if (!c.coachable.length) return '';
  return `<div class="ss-coach"><span class="ss-coach-h">Who to coach first</span><ul class="ss-coach-list">${c.coachable
    .map(
      (rep) =>
        `<li><span class="ss-coach-name">${escapeHtml(rep.name)}</span> <span class="ss-coach-meta">avg ${escapeHtml(r0(rep.avgFinal))} over ${escapeHtml(String(rep.calls))} call${rep.calls === 1 ? '' : 's'}</span></li>`,
    )
    .join('')}</ul></div>`;
}

// ==== Redesigned "snapshot" layout (bm-* = benchmark) ==============================================

interface OrgAgg { topReps: number; coreReps: number; calls: number; callTypes: number; topAvg: number; coreAvg: number; gap: number; upside: number; target: number; }

/** Org-wide top-vs-core position, aggregated across clusters weighted by call volume. */
function orgAgg(data: api.SecretSauceResponse, clusters: SecretSauceCluster[]): OrgAgg {
  let topW = 0, topSum = 0, coreW = 0, coreSum = 0;
  for (const c of clusters) {
    topSum += c.topFinal * c.topCalls; topW += c.topCalls;
    coreSum += c.coreFinal * c.coreCalls; coreW += c.coreCalls;
  }
  const topAvg = topW ? topSum / topW : 0;
  const coreAvg = coreW ? coreSum / coreW : 0;
  const gap = Math.max(0, topAvg - coreAvg);
  return {
    topReps: data.topAssigned ?? 0,
    coreReps: data.coreAssigned ?? 0,
    calls: (data.topCalls ?? topW) + (data.coreCalls ?? coreW),
    callTypes: clusters.length,
    topAvg, coreAvg, gap, upside: gap / 2, target: coreAvg + gap / 2,
  };
}

/** Hero "where you stand" (upside + gap side by side, on a 0-100 track) plus the four stat tiles. */
function heroHtml(a: OrgAgg): string {
  const clamp = (v: number): number => Math.max(0, Math.min(100, v));
  const coreP = clamp(a.coreAvg), topP = clamp(a.topAvg), tgtP = clamp(a.target);
  const fillW = Math.max(0, topP - coreP);
  return `
  <div class="bm-top">
    <div class="bm-hero">
      <div class="bm-duo">
        <div class="bm-fig bm-up">
          <div class="bm-fig-row"><b>+${escapeHtml(r0(a.upside))}</b><span class="bm-cap">pts within reach</span></div>
          <em>Move your <b>${escapeHtml(String(a.coreReps))} core rep${a.coreReps === 1 ? '' : 's'}</b> halfway to your top reps</em>
        </div>
        <span class="bm-vs"></span>
        <div class="bm-fig bm-gap">
          <div class="bm-fig-row"><b>${escapeHtml(r0(a.gap))}</b><span class="bm-cap">pt gap today</span></div>
          <em>Core ${escapeHtml(r0(a.coreAvg))} vs top ${escapeHtml(r0(a.topAvg))}</em>
        </div>
      </div>
      <div class="bm-track">
        <div class="bm-fill" data-left="${coreP.toFixed(2)}" data-width="${fillW.toFixed(2)}"></div>
        <div class="bm-mk bm-mk-core" data-left="${coreP.toFixed(2)}" data-l="Core ${escapeHtml(r0(a.coreAvg))}"></div>
        <div class="bm-mk bm-mk-tgt" data-left="${tgtP.toFixed(2)}" data-l="Target ${escapeHtml(r0(a.target))}"></div>
        <div class="bm-mk bm-mk-top" data-left="${topP.toFixed(2)}" data-l="Top ${escapeHtml(r0(a.topAvg))}"></div>
      </div>
      <div class="bm-scale"><span>0</span><span>25</span><span>50</span><span>75</span><span>100</span></div>
    </div>
    <div class="bm-stats">
      <div class="bm-stat"><b class="bm-teal">${escapeHtml(String(a.topReps))}</b><small>Top reps</small></div>
      <div class="bm-stat"><b>${escapeHtml(String(a.coreReps))}</b><small>Core reps</small></div>
      <div class="bm-stat"><b>${escapeHtml(String(a.calls))}</b><small>Calls analyzed</small></div>
      <div class="bm-stat"><b>${escapeHtml(String(a.callTypes))}</b><small>Call types</small></div>
    </div>
  </div>`;
}

/** A section heading: a gold tab, the title, and a right-aligned note. */
function sectionHeadHtml(title: string, note: string, gold = true): string {
  return `<div class="bm-sec"><span class="bm-tab${gold ? ' bm-tab-gold' : ''}"></span><h2 class="bm-sec-h">${escapeHtml(title)}</h2><span class="bm-sec-note">${escapeHtml(note)}</span></div>`;
}

/** Colour tier CLASS for a single top-minus-core gap cell (green matched -> red big gap). A class, not
 *  an inline style, so the bundle stays CSP-clean (no style="" attributes). */
function heatCell(g: number | null): { cls: string; t: string } {
  if (g == null || g < 0.15) return { cls: 'bm-z', t: '·' };
  const cls = g >= 1.2 ? 'bm-t4' : g >= 0.8 ? 'bm-t3' : g >= 0.45 ? 'bm-t2' : 'bm-t1';
  return { cls, t: g.toFixed(1) };
}

// The CRO picks one macro-group; the heatmap's columns are the scorecard clusters that belong to it (the
// SAME clusters the engine already produces — early-stage calls stay merged into "First buyer-seller
// engagement calls"). Every group cluster is a fixed column; ones with no scored calls read as a dash.
const CALLTYPE_GROUPS: readonly { key: string; label: string; clusters: readonly string[] }[] = [
  { key: 'engage', label: 'First Buyer-Seller Engagements', clusters: ['Cold', 'Inbound', 'ProductDemo'] },
  { key: 'validation', label: 'Validation', clusters: ['TechVal', 'Commercial'] },
  { key: 'success', label: 'Customer Success', clusters: ['Onboard', 'QBR', 'Adoption', 'Escalation', 'Expansion'] },
];

/** The group to show first: the one whose clusters carry the most scored calls today. */
function defaultGroupKey(clusters: SecretSauceCluster[]): string {
  const calls = new Map(clusters.map((c) => [c.group, c.topCalls + c.coreCalls]));
  let best = CALLTYPE_GROUPS[0]!.key, bestScore = -1;
  for (const g of CALLTYPE_GROUPS) {
    const score = g.clusters.reduce((s, k) => s + (calls.get(k) ?? 0), 0);
    if (score > bestScore) { bestScore = score; best = g.key; }
  }
  return best;
}

/** One heatmap for a selected macro-group: skills (rows, hottest first) x the group's clusters (fixed
 *  columns; a cluster with no data reads as a dash), cell = the top-minus-core gap. */
function heatmapHtml(clusters: SecretSauceCluster[], groupKey: string): string {
  const legend = `<div class="bm-legend"><span class="bm-legend-say">Bigger gap = your A-players pull further ahead here.</span>`
    + `<span class="bm-sw"><i class="bm-t1"></i>matched</span><span class="bm-sw"><i class="bm-t2"></i>notable</span>`
    + `<span class="bm-sw"><i class="bm-t3"></i>large</span><span class="bm-sw"><i class="bm-t4"></i>big gap</span></div>`;
  const group = CALLTYPE_GROUPS.find((g) => g.key === groupKey) ?? CALLTYPE_GROUPS[0]!;
  const byKey = new Map(clusters.map((c) => [c.group, c] as const));
  const cols = group.clusters; // fixed column set, whether or not each has data
  if (!cols.some((k) => byKey.has(k))) {
    return `<div class="bm-heat"><p class="bm-empty">No scored calls in this group yet — pick another group above, or score more calls of these types.</p></div>`;
  }
  const agg = new Map<string, { sum: number; n: number; label: string }>();
  const perCol = cols.map((k) => {
    const c = byKey.get(k);
    const m = new Map<string, number>();
    if (c) for (const gp of c.gaps) {
      m.set(gp.skillId, gp.gap);
      const cur = agg.get(gp.skillId) ?? { sum: 0, n: 0, label: skillLabelById(gp.skillId) };
      cur.sum += gp.gap; cur.n += 1; agg.set(gp.skillId, cur);
    }
    return m;
  });
  const rows = [...agg.entries()].sort((x, y) => (y[1].sum / y[1].n) - (x[1].sum / x[1].n)).map(([id, v]) => ({ id, label: v.label }));
  // Concatenation with backtick-containing maps so the output-encoding tripwire sees escaped nested
  // literals rather than bare HTML-holding variables.
  return `<div class="bm-heat"><div class="bm-heat-scroll"><table class="bm-hm">`
    + `<tr><th class="bm-rowh">Skill</th>${cols.map((k) => `<th>${escapeHtml(groupLabel(k))}</th>`).join('')}</tr>`
    + rows.map((row) => `<tr><th class="bm-rowh">${escapeHtml(row.label)}</th>${perCol.map((m) => {
        const c = heatCell(m.has(row.id) ? (m.get(row.id) as number) : null);
        return `<td class="${escapeHtml(c.cls)}">${escapeHtml(c.t)}</td>`;
      }).join('')}</tr>`).join('')
    + `</table></div>` + legend + `</div>`;
}

/** The heatmap section: the "Where the gaps live" header with the group dropdown, then the heatmap. */
function heatSectionInner(clusters: SecretSauceCluster[], groupKey: string): string {
  return `<div class="bm-sec"><span class="bm-tab bm-tab-gold"></span><h2 class="bm-sec-h">Where the gaps live</h2>`
    + `<label class="bm-heat-pick"><span class="bm-pick-lbl">Call-type group</span>`
    + `<select class="bm-select" id="bm-group-select">${CALLTYPE_GROUPS.map((g) => `<option value="${escapeHtml(g.key)}"${g.key === groupKey ? ' selected' : ''}>${escapeHtml(g.label)}</option>`).join('')}</select></label></div>`
    + heatmapHtml(clusters, groupKey);
}

/** Fill (or re-fill on dropdown change) the heatmap section container. */
function paintHeat(root: HTMLElement, clusters: SecretSauceCluster[], groupKey: string): void {
  const wrap = root.querySelector<HTMLElement>('#bm-heat-wrap');
  if (!wrap) return;
  wrap.innerHTML = heatSectionInner(clusters, groupKey);
  const sel = wrap.querySelector<HTMLSelectElement>('#bm-group-select');
  sel?.addEventListener('change', () => paintHeat(root, clusters, sel.value));
}

/** Coach-first: the core reps furthest behind, aggregated across call types (worst avg kept), top 3. */
function coachFirstHtml(clusters: SecretSauceCluster[]): string {
  const byName = new Map<string, { name: string; avg: number; calls: number; label: string }>();
  for (const c of clusters) {
    for (const rep of c.coachable) {
      const cur = byName.get(rep.name);
      if (!cur || rep.avgFinal < cur.avg) byName.set(rep.name, { name: rep.name, avg: rep.avgFinal, calls: rep.calls, label: groupLabel(c.group) });
    }
  }
  const top3 = [...byName.values()].sort((a, b) => a.avg - b.avg).slice(0, 3);
  if (!top3.length) return `<p class="bm-empty">No core reps are lagging far enough to flag right now.</p>`;
  return `<div class="bm-coach">${top3.map((r) => `
    <div class="bm-rep">
      <div class="bm-rep-nm">${escapeHtml(r.name)}</div>
      <div class="bm-rep-sc">${escapeHtml(r0(r.avg))}</div>
      <div class="bm-rep-mt">avg / 100 · ${escapeHtml(String(r.calls))} call${r.calls === 1 ? '' : 's'}</div>
      <div class="bm-rep-fx">Behind on <b>${escapeHtml(r.label)}</b></div>
    </div>`).join('')}</div>`;
}

/** The org-wide top-3 differentiators: the skills where top reps pull furthest ahead of core, averaged
 *  across every call type (call-weighted). These are the "critical findings" headline. */
interface Finding { skillId: string; label: string; topAvg: number; coreAvg: number; gap: number; }
function topFindings(clusters: SecretSauceCluster[]): Finding[] {
  const agg = new Map<string, { topSum: number; topW: number; coreSum: number; coreW: number; label: string }>();
  for (const c of clusters) {
    for (const g of c.gaps) {
      const cur = agg.get(g.skillId) ?? { topSum: 0, topW: 0, coreSum: 0, coreW: 0, label: skillLabelById(g.skillId) };
      cur.topSum += g.topAvg * c.topCalls; cur.topW += c.topCalls;
      cur.coreSum += g.coreAvg * c.coreCalls; cur.coreW += c.coreCalls;
      agg.set(g.skillId, cur);
    }
  }
  const out: Finding[] = [];
  for (const [skillId, v] of agg) {
    const topAvg = v.topW ? v.topSum / v.topW : 0;
    const coreAvg = v.coreW ? v.coreSum / v.coreW : 0;
    out.push({ skillId, label: v.label, topAvg, coreAvg, gap: Math.max(0, topAvg - coreAvg) });
  }
  return out.sort((a, b) => b.gap - a.gap).slice(0, 3);
}

/** The three critical-finding cards: skill + a 0-5 Top-vs-Core twin bar + the gap. Bar widths are applied
 *  from data-w in JS (CSP: no inline style=""). */
function findingsHtml(clusters: SecretSauceCluster[]): string {
  const f = topFindings(clusters);
  if (!f.length) return `<p class="bm-empty">Not enough matched calls yet to surface what sets your top reps apart.</p>`;
  return `<div class="bm-find">${f
    .map(
      (x, i) => `
    <div class="bm-find-card">
      <div class="bm-find-top-row"><span class="bm-find-rank">${i + 1}</span><span class="bm-find-skill">${escapeHtml(x.label)}</span><span class="bm-find-gap"><b>+${x.gap.toFixed(1)}</b> gap</span></div>
      <div class="bm-find-bar"><span class="bm-find-k">Top</span><span class="bm-find-tr"><span class="bm-find-fl bm-find-top" data-w="${((x.topAvg / 5) * 100).toFixed(1)}"></span></span><b>${x.topAvg.toFixed(1)}</b></div>
      <div class="bm-find-bar"><span class="bm-find-k">Core</span><span class="bm-find-tr"><span class="bm-find-fl bm-find-core" data-w="${((x.coreAvg / 5) * 100).toFixed(1)}"></span></span><b>${x.coreAvg.toFixed(1)}</b></div>
    </div>`,
    )
    .join('')}</div>`;
}

/** One collapsed call-type row in the drill-down: the table + upside + coach + playbook (lazy). */
function ddClusterHtml(c: SecretSauceCluster): string {
  const label = groupLabel(c.group);
  const delta = Math.round(c.topFinal - c.coreFinal);
  const cached = synthByGroup.get(c.group);
  let coaching = '';
  if (cached?.state === 'done' && cached.synth) {
    coaching = `<div class="pb-coaching ss-play" data-coaching="${escapeHtml(c.group)}">${playBlockHtml(c, cached.synth, { writtenAt: cached.writtenAt, stale: cached.stale })}</div>`;
  } else {
    coaching = `<div class="pb-coaching" data-coaching="${escapeHtml(c.group)}"><p class="pb-coaching-intro bm-play-hint">Open this call type to write its coaching playbook.</p></div>`;
  }
  return `
    <details class="bm-dd" data-cluster="${escapeHtml(c.group)}">
      <summary class="bm-dd-sum"><span class="bm-dd-car" aria-hidden="true">▸</span><span class="bm-dd-title">${escapeHtml(label)}</span><span class="bm-dd-meta">Top ${escapeHtml(String(c.topCalls))} · Core ${escapeHtml(String(c.coreCalls))} calls · ${escapeHtml(String(delta))}-pt gap</span></summary>
      <div class="bm-dd-body">
        ${comparisonTableHtml(c.gaps)}
        <p class="pb-cmp-note">${escapeHtml(CMP_NOTE)}</p>
        ${upsideHtml(c)}
        ${coachHtml(c)}
        ${coaching}
      </div>
    </details>`;
}

/** Wire the collapse toggle + Copy + Download on a revealed coaching play. Reads the synth from cache. */
function wirePlayControls(box: HTMLElement, group: string): void {
  const toggle = box.querySelector<HTMLButtonElement>('[data-toggle]');
  const body = box.querySelector<HTMLElement>('.ss-play-body');
  const caret = box.querySelector<HTMLElement>('.ss-play-caret');
  toggle?.addEventListener('click', () => {
    const collapsed = body?.hasAttribute('hidden');
    if (collapsed) body?.removeAttribute('hidden');
    else body?.setAttribute('hidden', '');
    toggle.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
    if (caret) caret.textContent = collapsed ? '▾' : '▸';
  });

  const text = (): string => {
    const s = synthByGroup.get(group)?.synth;
    return s ? synthToText(groupLabel(group), s) : '';
  };
  const flash = (btn: HTMLButtonElement, msg: string) => {
    const prev = btn.textContent;
    btn.textContent = msg;
    btn.disabled = true;
    setTimeout(() => {
      btn.textContent = prev;
      btn.disabled = false;
    }, 1500);
  };

  const copyBtn = box.querySelector<HTMLButtonElement>('[data-copy]');
  copyBtn?.addEventListener('click', () => {
    void navigator.clipboard.writeText(text()).then(
      () => flash(copyBtn, 'Copied ✓'),
      () => flash(copyBtn, 'Copy failed'),
    );
  });

  const dlBtn = box.querySelector<HTMLButtonElement>('[data-dl]');
  dlBtn?.addEventListener('click', () => {
    const blob = new Blob([text()], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `benchmark-${group.toLowerCase()}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
}

/** Write (or rewrite) a cluster's coaching playbook. The server persists it (keyed by the cluster
 *  group), so the next page load shows it by default; `writtenAt` refreshes "Last updated". */
async function revealPlay(group: string, cluster: SecretSauceCluster): Promise<void> {
  const box = mounted?.querySelector<HTMLElement>(`[data-coaching="${CSS.escape(group)}"]`);
  if (!box) return;
  if (synthByGroup.get(group)?.state === 'loading') return; // don't double-fire
  synthByGroup.set(group, { state: 'loading', synth: null });
  box.hidden = false;
  box.classList.remove('ss-play');
  box.innerHTML = LOADING_HTML;
  try {
    const { synthesis, writtenAt } = await api.synthesizePlaybook(
      groupLabel(group),
      cluster.gaps.map((g) => ({ skillName: g.skillName, topAvg: g.topAvg, coreAvg: g.coreAvg, gap: g.gap })),
      group,
    );
    synthByGroup.set(group, { state: 'done', synth: synthesis, writtenAt, stale: false });
    box.classList.add('ss-play');
    box.innerHTML = playBlockHtml(cluster, synthesis, { writtenAt, stale: false });
    wirePlayControls(box, group);
  } catch {
    synthByGroup.set(group, { state: 'error', synth: null });
    box.classList.remove('ss-play');
    box.innerHTML =
      '<div class="ss-reveal-row"><button type="button" class="pb-generate ss-reveal" data-regen>Generate the coaching playbook</button><span class="ss-reveal-hint">Couldn\'t write it just now — the table above is still yours. Try again.</span></div>';
  }
}

/** Render the Secret Sauce page. Called each time the view opens. */
export async function renderSecretSauce(): Promise<void> {
  const root = document.getElementById('ss-app');
  if (!root) return;
  mounted = root;
  root.innerHTML = '<p class="ss-loading">Reading your scored calls…</p>';

  let data: api.SecretSauceResponse;
  try {
    data = await api.getSecretSauce();
  } catch {
    root.innerHTML = messageHtml(
      'Benchmark is unavailable right now',
      'We couldn\'t read your scored calls just now. Please try again in a few minutes.',
    );
    return;
  }

  if (data.trendsOff) {
    root.innerHTML = messageHtml(
      'Turn on cross-call analytics',
      'Benchmark compares your top and core reps across their scored calls. Enable cross-call analytics in <strong>Settings</strong> to switch it on.',
      { label: 'Open Settings', view: 'account' },
    );
    wireNav(root);
    return;
  }

  if (data.needsClassification) {
    const body =
      data.reason === 'unassigned'
        ? 'On the <strong>Teams</strong> page, mark your strongest reps as <strong>Top Rep</strong> and the cohort you want to lift as <strong>Core Rep</strong>. Benchmark does the rest automatically, behind the scenes, with no action needed from you. Come back to this page any time to see how the picture is developing.'
        : 'Benchmark needs two rep classes — <strong>Top Rep</strong> and <strong>Core Rep</strong>. Assign reps to them on the <strong>Teams</strong> page and this page fills in on its own. This manual step is the stand-in for the CRM signal that will drive it automatically later.';
    root.innerHTML = messageHtml('Classify your reps to unlock Benchmark', body, { label: 'Go to Teams', view: 'teams' });
    wireNav(root);
    return;
  }

  const clusters = data.clusters ?? [];
  if (data.unavailable || clusters.length === 0) {
    root.innerHTML = messageHtml(
      'Not enough scored calls yet',
      `Once your Top and Core reps have enough scored calls of the same type (at least ${escapeHtml(String(data.minN ?? 5))} each), the per-skill differences show up here — grouped by call type so the comparison is fair.`,
    );
    return;
  }

  // Seed always-on state from the server's persisted playbooks (authoritative). A cluster with no stored
  // playbook — and none written this session — stays idle and is auto-written just below.
  for (const c of clusters) {
    if (c.playbook) {
      synthByGroup.set(c.group, { state: 'done', synth: c.playbook, writtenAt: c.playbookWrittenAt ?? null, stale: !!c.playbookStale });
    }
  }

  const agg = orgAgg(data, clusters);
  const toolbar = `<div class="ss-toolbar bm-toolbar"><button type="button" class="ss-play-act ss-pdf" data-pdf>Download full report (PDF)</button></div>`;
  root.innerHTML =
    sectionHeadHtml('What your top reps do differently', 'The three skills where your A-players pull furthest ahead') +
    findingsHtml(clusters) +
    sectionHeadHtml('Where you stand', 'Your core team vs your top reps today') +
    heroHtml(agg) +
    sectionHeadHtml('Coach first', 'Core reps furthest behind their cohort') +
    coachFirstHtml(clusters) +
    '<div id="bm-heat-wrap"></div>' +
    sectionHeadHtml('Full breakdown & playbooks', 'Collapsed · open a call type for its table and playbook', false) +
    toolbar +
    '<div class="bm-dd-list">' + clusters.map((c) => ddClusterHtml(c)).join('') + '</div>';

  // Paint the group-selectable heatmap into its container and wire the dropdown.
  paintHeat(root, clusters, defaultGroupKey(clusters));

  // Position the hero fill + markers, and the finding twin-bars, from their data-* (kept out of inline
  // style="" for CSP).
  const fillEl = root.querySelector<HTMLElement>('.bm-fill');
  if (fillEl) { fillEl.style.left = `${fillEl.dataset.left ?? 0}%`; fillEl.style.width = `${fillEl.dataset.width ?? 0}%`; }
  root.querySelectorAll<HTMLElement>('.bm-mk').forEach((m) => { if (m.dataset.left) m.style.left = `${m.dataset.left}%`; });
  root.querySelectorAll<HTMLElement>('.bm-find-fl').forEach((el) => { if (el.dataset.w) el.style.width = `${el.dataset.w}%`; });

  clusterByGroupRef = new Map(clusters.map((c) => [c.group, c] as const));

  // One delegated handler for Regenerate — wired ONCE per #ss-app (it survives box re-renders, and
  // re-opening the view must not stack duplicate listeners).
  if (!root.dataset.ssWired) {
    root.dataset.ssWired = '1';
    root.addEventListener('click', (e) => {
      const el = (e.target as HTMLElement | null)?.closest('[data-regen], [data-reveal]');
      if (!el) return;
      const g = el.closest('.bm-dd')?.getAttribute('data-cluster') ?? '';
      const cl = clusterByGroupRef.get(g);
      if (cl) void revealPlay(g, cl);
    });
  }

  // Server-persisted playbooks are wired now (they render inside the collapsed row). The rest are LAZY:
  // a cluster's playbook is written only the first time its drill-down row is opened, so the snapshot
  // loads without firing a model call per call type.
  for (const c of clusters) {
    if (synthByGroup.get(c.group)?.state === 'done') {
      const box = root.querySelector<HTMLElement>(`[data-coaching="${CSS.escape(c.group)}"]`);
      if (box) wirePlayControls(box, c.group);
    }
  }
  root.querySelectorAll<HTMLDetailsElement>('.bm-dd[data-cluster]').forEach((d) => {
    d.addEventListener('toggle', () => {
      if (!d.open) return;
      const g = d.getAttribute('data-cluster') ?? '';
      const cl = clusterByGroupRef.get(g);
      const st = synthByGroup.get(g)?.state;
      if (cl && st !== 'done' && st !== 'loading') void revealPlay(g, cl);
    });
  });

  // Wire the top-level PDF export.
  root.querySelector<HTMLButtonElement>('[data-pdf]')?.addEventListener('click', () => void downloadPdf(clusters));

  wireNav(root);
}

/** Build the full report into the shared print sheet (#report) and print. Includes every cluster's
 *  table, upside, and coachable reps, plus any coaching play already revealed on screen. */
async function downloadPdf(clusters: SecretSauceCluster[]): Promise<void> {
  const report = document.getElementById('report');
  if (!report) return;
  report.innerHTML = '';
  const sheet = document.createElement('div');
  sheet.className = 'rep-page';
  sheet.appendChild(reportMasthead('Benchmark'));
  const sub = document.createElement('p');
  sub.className = 'rep-blurb';
  sub.textContent = 'What your top reps do differently, by call type.';
  sheet.appendChild(sub);

  for (const c of clusters) {
    const sec = document.createElement('div');
    sec.className = 'rep-section';
    const cached = synthByGroup.get(c.group);
    const play =
      cached?.state === 'done' && cached.synth ? `<div class="pb-coaching">${coachingInnerHtml(cached.synth, scoreMap(c.gaps))}</div>` : '';
    sec.innerHTML = `<h2 class="rep-h">${escapeHtml(groupLabel(c.group))}</h2>${comparisonTableHtml(c.gaps)}<p class="pb-cmp-note">${escapeHtml(CMP_NOTE)}</p>${upsideHtml(c)}${coachHtml(c)}${play}`;
    sheet.appendChild(sec);
  }

  sheet.appendChild(renderClose());
  report.appendChild(sheet);
  report.appendChild(renderFooter());
  await waitForReportImages(report);
  window.print();
}

/** Bind the [data-nav] CTA buttons (injected after main.ts's one-time nav pass, so bind directly). */
function wireNav(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>('[data-nav]').forEach((b) =>
    b.addEventListener('click', () => navigateTo(b.dataset.nav || 'home')),
  );
}
